import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for lazy loading images using Intersection Observer
 * @param {Object} options - Configuration options for intersection observer
 * @returns {Object} - Object containing ref and loading state
 */
export const useLazyLoading = (options = {}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef();

  const defaultOptions = {
    threshold: 0.1,
    rootMargin: '50px 0px',
    ...options
  };

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, defaultOptions);

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return {
    imgRef,
    isInView,
    isLoaded,
    setIsLoaded
  };
};

/**
 * Custom hook for lazy loading with batch processing
 * Used for loading images in groups to avoid overwhelming the browser
 */
export const useBatchLazyLoading = (items, batchSize = 10) => {
  const [visibleItems, setVisibleItems] = useState([]);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Initialize with first batch
  useEffect(() => {
    if (items.length > 0) {
      setVisibleItems(items.slice(0, batchSize));
      setCurrentBatch(1);
    }
  }, [items, batchSize]);

  const loadNextBatch = () => {
    if (isLoadingMore || visibleItems.length >= items.length) return;

    setIsLoadingMore(true);

    setTimeout(() => {
      const nextBatch = items.slice(0, (currentBatch + 1) * batchSize);
      setVisibleItems(nextBatch);
      setCurrentBatch(prev => prev + 1);
      setIsLoadingMore(false);
    }, 100); // Small delay to smooth the loading experience
  };

  const hasMore = visibleItems.length < items.length;

  return {
    visibleItems,
    loadNextBatch,
    hasMore,
    isLoadingMore
  };
};